/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame } from "../../../shared/contracts";
import { EventProxy } from "../../core/event-proxy";
import type { IAnimatable } from "../../core/ianimatable";
import type { IHittable } from "../../core/ihittable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import { BumperAnimation } from "./bumper-animation";
import type { BumperData } from "./bumper-data";
import { BumperHit } from "./bumper-hit";
import { BumperState } from "./bumper-state";

/**
 * A pop bumper.
 */
export class Bumper extends Item<BumperData> implements IHittable, IAnimatable {
  private readonly state: BumperState;
  private hit?: BumperHit;
  private animation?: BumperAnimation;

  public constructor(data: BumperData) {
    super(data);
    this.state = new BumperState(this.getName(), data.isRingVisible);
  }

  public getState(): BumperState {
    return this.state;
  }

  public isCollidable(): boolean {
    return this.data.isCollidable;
  }

  public setupPlayer(player: Player, table: Table): void {
    const height = table.getSurfaceHeight(
      this.data.szSurface,
      this.data.center.x,
      this.data.center.y,
    );
    this.events = new EventProxy(this, "bumper", player.getEventSink());
    this.animation = new BumperAnimation(this.data, this.state);
    this.hit = new BumperHit(this.data, this.animation, this.events, height);
  }

  public getHitShapes(): HitObject[] {
    return [this.hit!];
  }

  public getAnimation(): BumperAnimation {
    return this.animation!;
  }

  public toFrame(): ItemFrame {
    return this.state.toFrame();
  }
}
